import { LocalCodingAgentRuntime } from '@this+that/models';

import {
  ASSISTANT_COMPOSER_TARGET,
  DelegatePanelHandOff,
  DoBoxDelegateComposerEntryState,
  DoBoxDelegateComposerState,
  DoBoxDelegateComposerTarget,
  HandlerSet,
} from './types';
import { DoBoxDelegateComposerTargetKind } from '../../enums';
import { AGENT_PROFILE_GENERIC_VALUE } from '../../pages/private/DoBox/components/DoBoxDelegateMenu/constants';
import { getDefaultModelForRuntime } from '../../pages/private/DoBox/components/DoBoxDelegateMenu/delegateRuntimeModels';

const DEFAULT_LOCAL_RUNTIME = LocalCodingAgentRuntime.CLAUDE;

export const DEFAULT_COMPOSER_ENTRY_STATE: DoBoxDelegateComposerEntryState = {
  target: ASSISTANT_COMPOSER_TARGET,
  planFirst: true,
  localRuntime: DEFAULT_LOCAL_RUNTIME,
  localModel: getDefaultModelForRuntime(DEFAULT_LOCAL_RUNTIME),
  localAgentProfile: AGENT_PROFILE_GENERIC_VALUE,
  focusNonce: 0,
};

function resolveEntry(
  state: DoBoxDelegateComposerState,
  entryId: string,
): DoBoxDelegateComposerEntryState {
  return state.stateByEntryId[entryId] ?? DEFAULT_COMPOSER_ENTRY_STATE;
}

function updateEntry(
  set: HandlerSet,
  entryId: string,
  updater: (
    current: DoBoxDelegateComposerEntryState,
  ) => Partial<DoBoxDelegateComposerEntryState>,
) {
  set((state) => {
    const current = resolveEntry(state, entryId);
    return {
      stateByEntryId: {
        ...state.stateByEntryId,
        [entryId]: { ...current, ...updater(current) },
      },
    };
  });
}

export const handlePresetDelegateTarget = (
  set: HandlerSet,
  entryId: string,
  target: DoBoxDelegateComposerTarget,
  planFirst: boolean,
) => {
  updateEntry(set, entryId, (current) => ({
    target,
    planFirst,
    focusNonce: current.focusNonce + 1,
  }));
};

const DEFAULT_LOCAL_CLAUDE_TARGET: DoBoxDelegateComposerTarget = {
  kind: DoBoxDelegateComposerTargetKind.LOCAL_CLAUDE,
};

export const handleRequestDelegate = (
  set: HandlerSet,
  entryId: string,
  target?: DoBoxDelegateComposerTarget,
) => {
  set((state) => {
    const preferredTarget =
      target ??
      state.lastAgentByEntryId[entryId] ??
      state.lastAgent ??
      DEFAULT_LOCAL_CLAUDE_TARGET;
    const current = resolveEntry(state, entryId);
    return {
      stateByEntryId: {
        ...state.stateByEntryId,
        [entryId]: {
          ...current,
          target: preferredTarget,
          planFirst: true,
          focusNonce: current.focusNonce + 1,
        },
      },
    };
  });
};

export const handleSetTarget = (
  set: HandlerSet,
  entryId: string,
  target: DoBoxDelegateComposerTarget,
) => {
  updateEntry(set, entryId, () => ({ target }));
};

export const handleSetPlanFirst = (
  set: HandlerSet,
  entryId: string,
  planFirst: boolean,
) => {
  updateEntry(set, entryId, () => ({ planFirst }));
};

export const handleSetLocalRuntime = (
  set: HandlerSet,
  entryId: string,
  runtime: LocalCodingAgentRuntime,
) => {
  updateEntry(set, entryId, () => ({
    localRuntime: runtime,
    localModel: getDefaultModelForRuntime(runtime),
  }));
};

export const handleSetLocalModel = (
  set: HandlerSet,
  entryId: string,
  model: string,
) => {
  updateEntry(set, entryId, () => ({ localModel: model }));
};

export const handleSetLocalAgentProfile = (
  set: HandlerSet,
  entryId: string,
  profile: string,
) => {
  updateEntry(set, entryId, () => ({ localAgentProfile: profile }));
};

export const handleRecordAgentTarget = (
  set: HandlerSet,
  entryId: string,
  target: DoBoxDelegateComposerTarget,
) => {
  if (
    target.kind !== DoBoxDelegateComposerTargetKind.AGENT ||
    !target.agentTemplateId
  ) {
    return;
  }
  set((state) => ({
    lastAgentByEntryId: { ...state.lastAgentByEntryId, [entryId]: target },
    lastAgent: target,
  }));
};

export const handleRequestFocus = (set: HandlerSet, entryId: string) => {
  updateEntry(set, entryId, (current) => ({
    focusNonce: current.focusNonce + 1,
  }));
};

export const handleClearEntry = (set: HandlerSet, entryId: string) => {
  set((state) => {
    const { [entryId]: _removed, ...remaining } = state.stateByEntryId;
    return { stateByEntryId: remaining };
  });
};

export const handleHandOffToPanel = (
  set: HandlerSet,
  entryId: string,
  { target, planFirst, draft }: DelegatePanelHandOff,
) => {
  set((state) => {
    const current = resolveEntry(state, entryId);
    return {
      stateByEntryId: {
        ...state.stateByEntryId,
        [entryId]: {
          ...current,
          target,
          planFirst,
          focusNonce: current.focusNonce + 1,
        },
      },
      pendingDraftByEntryId:
        draft === ''
          ? state.pendingDraftByEntryId
          : { ...state.pendingDraftByEntryId, [entryId]: draft },
    };
  });
};

export const handleConsumeDraft = (
  set: HandlerSet,
  currentDrafts: Record<string, string>,
  entryId: string,
): string | null => {
  const draft = currentDrafts[entryId];
  if (draft === undefined) {
    return null;
  }
  set((state) => {
    const { [entryId]: _removed, ...remaining } = state.pendingDraftByEntryId;
    return { pendingDraftByEntryId: remaining };
  });
  return draft;
};

export const handleRequestBandFocus = (set: HandlerSet, entryId: string) => {
  set({ bandFocusEntryId: entryId });
};

export const handleClearBandFocus = (set: HandlerSet, entryId: string) => {
  set((state) =>
    state.bandFocusEntryId === entryId ? { bandFocusEntryId: null } : {},
  );
};
